/**
 * LLM Analysis Service
 *
 * Provides LLM-powered URL/link analysis with graceful fallback to simple heuristics.
 * Uses OpenRouter API for intelligent text analysis.
 *
 * Design principles:
 * - LLM-first: Try LLM analysis when available
 * - Graceful degradation: Fall back to string-based heuristics on failure
 * - No regex: All fallbacks use simple string methods (startsWith, includes, split)
 * - Sync exports: Provide sync versions for immediate use (fallback only)
 */

import { openRouter } from './openRouter';
import type { OpenRouterMessage } from '@/types';

// ============================================
// TYPES
// ============================================

export type LinkType = 'youtube' | 'twitter' | 'spotify' | 'podcast' | 'generic';

export interface UrlAnalysisResult {
  isUrl: boolean;
  normalizedUrl: string | null;
  linkType: LinkType;
  videoId: string | null;
  metadataHints: {
    possibleTitle?: string;
    platform?: string;
  };
  source: 'llm' | 'fallback';
}

// ============================================
// CONSTANTS
// ============================================

const LLM_TIMEOUT_MS = 5000;
const MAX_INPUT_LENGTH = 2000;

// ============================================
// LLM PROMPT
// ============================================

const URL_ANALYSIS_SYSTEM_PROMPT = `You are a URL analysis assistant. You analyze text to determine if it's a URL and classify it. Always respond with valid JSON only, no markdown or explanation.`;

function createAnalysisPrompt(input: string): string {
  return `Analyze the following text and determine if it's a URL.

Text: "${input}"

Respond with this exact JSON structure:
{
  "isUrl": boolean,
  "normalizedUrl": "the URL with https:// prefix if valid, null otherwise",
  "linkType": "youtube" | "twitter" | "spotify" | "podcast" | "generic",
  "videoId": "YouTube video ID (11 chars) if applicable, null otherwise",
  "metadataHints": {
    "possibleTitle": "inferred title if any",
    "platform": "platform name if identifiable"
  }
}

Link type rules:
- youtube: youtube.com, youtu.be
- twitter: twitter.com, x.com
- spotify: spotify.com (but "podcast" if /episode or /show path)
- podcast: podcasts.apple.com, pocketcasts.com, overcast.fm, castro.fm, podcasts.google.com
- generic: everything else`;
}

// ============================================
// FALLBACK FUNCTIONS (NO REGEX)
// ============================================

/**
 * Simple URL detection without regex.
 * Uses string methods: startsWith, includes
 */
function fallbackIsUrl(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed || trimmed.length > MAX_INPUT_LENGTH) {
    return false;
  }

  const lower = trimmed.toLowerCase();

  // Check for protocol prefix
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return true;
  }

  // Check for www prefix
  if (lower.startsWith('www.')) {
    return true;
  }

  // Check for domain-like pattern (contains dot, no spaces, reasonable length)
  if (lower.includes('.') && !lower.includes(' ') && lower.length < 500) {
    // Basic validation: has a TLD-like ending
    const parts = lower.split('.');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length >= 2 && lastPart.length <= 10) {
      return true;
    }
  }

  return false;
}

/**
 * Simple URL normalization without regex.
 * Adds https:// if no protocol present.
 */
function fallbackNormalizeUrl(url: string): string {
  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  // Already has protocol
  if (lower.startsWith('https://') || lower.startsWith('http://')) {
    return trimmed;
  }

  // Add https:// to www. URLs
  if (lower.startsWith('www.')) {
    return `https://${trimmed}`;
  }

  // Add https:// to bare domains
  return `https://${trimmed}`;
}

/**
 * Simple link type detection without regex.
 * Uses string includes() method.
 */
function fallbackDetectLinkType(url: string): LinkType {
  const lower = url.toLowerCase();

  // YouTube
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) {
    return 'youtube';
  }

  // Twitter/X
  if (lower.includes('twitter.com') || lower.includes('x.com')) {
    return 'twitter';
  }

  // Podcast platforms - check BEFORE general spotify
  const podcastIndicators = [
    'podcasts.apple.com',
    'open.spotify.com/episode',
    'open.spotify.com/show',
    'pocketcasts.com',
    'overcast.fm',
    'castro.fm',
    'podcasts.google.com',
  ];

  for (const indicator of podcastIndicators) {
    if (lower.includes(indicator)) {
      return 'podcast';
    }
  }

  // Spotify (general - tracks, albums, playlists)
  if (lower.includes('spotify.com')) {
    return 'spotify';
  }

  return 'generic';
}

/**
 * Simple YouTube video ID extraction without regex.
 * Uses string split() and substring().
 */
function fallbackGetYouTubeVideoId(url: string): string | null {
  const lower = url.toLowerCase();

  // Handle youtu.be format: https://youtu.be/VIDEO_ID
  if (lower.includes('youtu.be/')) {
    const parts = url.split('youtu.be/');
    if (parts[1]) {
      // Split on query/hash params and take first part
      const idPart = parts[1].split('?')[0]?.split('#')[0]?.split('&')[0];
      if (idPart && idPart.length >= 11) {
        return idPart.substring(0, 11);
      }
    }
  }

  // Handle youtube.com/watch?v= format
  if (lower.includes('v=')) {
    const vIndex = url.indexOf('v=');
    const remaining = url.substring(vIndex + 2);
    // Split on next param delimiter
    const idPart = remaining.split('&')[0]?.split('#')[0];
    if (idPart && idPart.length >= 11) {
      return idPart.substring(0, 11);
    }
  }

  // Handle youtube.com/embed/VIDEO_ID format
  if (lower.includes('/embed/')) {
    const parts = url.split('/embed/');
    if (parts[1]) {
      const idPart = parts[1].split('?')[0]?.split('#')[0]?.split('/')[0];
      if (idPart && idPart.length >= 11) {
        return idPart.substring(0, 11);
      }
    }
  }

  // Handle youtube.com/v/VIDEO_ID format
  if (lower.includes('/v/')) {
    const parts = url.split('/v/');
    if (parts[1]) {
      const idPart = parts[1].split('?')[0]?.split('#')[0]?.split('/')[0];
      if (idPart && idPart.length >= 11) {
        return idPart.substring(0, 11);
      }
    }
  }

  return null;
}

// ============================================
// HELPERS
// ============================================

/**
 * Create a fallback result using heuristic functions.
 */
function createFallbackResult(input: string): UrlAnalysisResult {
  const isUrl = fallbackIsUrl(input);

  if (!isUrl) {
    return {
      isUrl: false,
      normalizedUrl: null,
      linkType: 'generic',
      videoId: null,
      metadataHints: {},
      source: 'fallback',
    };
  }

  const normalizedUrl = fallbackNormalizeUrl(input);
  const linkType = fallbackDetectLinkType(normalizedUrl);
  const videoId = linkType === 'youtube' ? fallbackGetYouTubeVideoId(normalizedUrl) : null;

  return {
    isUrl: true,
    normalizedUrl,
    linkType,
    videoId,
    metadataHints: {},
    source: 'fallback',
  };
}

/**
 * Validate that a link type is one of the allowed values.
 */
function validateLinkType(type: unknown): LinkType {
  const valid: LinkType[] = ['youtube', 'twitter', 'spotify', 'podcast', 'generic'];
  return valid.includes(type as LinkType) ? (type as LinkType) : 'generic';
}

/**
 * Promise that rejects after a timeout.
 */
function timeout<T>(ms: number): Promise<T> {
  return new Promise((_, reject) => setTimeout(() => reject(new Error('LLM timeout')), ms));
}

/**
 * Call LLM for URL analysis.
 */
async function callLlmForAnalysis(input: string): Promise<UrlAnalysisResult | null> {
  const messages: OpenRouterMessage[] = [
    { role: 'system', content: URL_ANALYSIS_SYSTEM_PROMPT },
    { role: 'user', content: createAnalysisPrompt(input) },
  ];

  const response = await openRouter.chat(messages);

  try {
    // Clean potential markdown code blocks from response
    let jsonStr = response.trim();
    if (jsonStr.startsWith('```json')) {
      jsonStr = jsonStr.slice(7);
    } else if (jsonStr.startsWith('```')) {
      jsonStr = jsonStr.slice(3);
    }
    if (jsonStr.endsWith('```')) {
      jsonStr = jsonStr.slice(0, -3);
    }
    jsonStr = jsonStr.trim();

    const parsed = JSON.parse(jsonStr) as {
      isUrl?: boolean;
      normalizedUrl?: string | null;
      linkType?: string;
      videoId?: string | null;
      metadataHints?: { possibleTitle?: string; platform?: string };
    };

    return {
      isUrl: Boolean(parsed.isUrl),
      normalizedUrl: parsed.normalizedUrl || null,
      linkType: validateLinkType(parsed.linkType),
      videoId: parsed.videoId || null,
      metadataHints: parsed.metadataHints || {},
      source: 'llm',
    };
  } catch {
    // JSON parsing failed
    return null;
  }
}

// ============================================
// PUBLIC API - ASYNC (LLM + FALLBACK)
// ============================================

/**
 * Analyze text to determine if it's a URL and classify it.
 * Uses LLM when available, falls back to heuristics on failure.
 *
 * @param input - Text to analyze
 * @returns Analysis result with source indicator
 *
 * @example
 * const result = await analyzeUrl('youtube.com/watch?v=dQw4w9WgXcQ');
 * // result.isUrl === true
 * // result.linkType === 'youtube'
 * // result.videoId === 'dQw4w9WgXcQ'
 */
export async function analyzeUrl(input: string): Promise<UrlAnalysisResult> {
  const trimmed = input.trim();

  // Quick validation
  if (!trimmed || trimmed.length > MAX_INPUT_LENGTH) {
    return {
      isUrl: false,
      normalizedUrl: null,
      linkType: 'generic',
      videoId: null,
      metadataHints: {},
      source: 'fallback',
    };
  }

  // If OpenRouter is not configured, use fallback immediately
  if (!openRouter.isConfigured()) {
    return createFallbackResult(trimmed);
  }

  try {
    // Race LLM call against timeout
    const result = await Promise.race([
      callLlmForAnalysis(trimmed),
      timeout<UrlAnalysisResult | null>(LLM_TIMEOUT_MS),
    ]);

    if (result) {
      return result;
    }
  } catch (error) {
    console.warn('LLM analysis failed, using fallback:', error);
  }

  return createFallbackResult(trimmed);
}

// ============================================
// PUBLIC API - SYNC (FALLBACK ONLY)
// ============================================

/**
 * Synchronous URL detection using heuristics only.
 * Use this when you need an immediate answer without waiting for LLM.
 *
 * @param input - String to check
 * @returns True if the string appears to be a URL
 */
export function isUrlSync(input: string): boolean {
  return fallbackIsUrl(input);
}

/**
 * Synchronous URL normalization using heuristics only.
 * Adds https:// if no protocol present.
 *
 * @param url - URL string to normalize
 * @returns Normalized URL with protocol
 */
export function normalizeUrlSync(url: string): string {
  return fallbackNormalizeUrl(url);
}

/**
 * Synchronous link type detection using heuristics only.
 *
 * @param url - URL to analyze
 * @returns Link type
 */
export function detectLinkTypeSync(url: string): LinkType {
  return fallbackDetectLinkType(url);
}

/**
 * Synchronous YouTube video ID extraction using heuristics only.
 *
 * @param url - YouTube URL
 * @returns Video ID (11 characters) or null
 */
export function getYouTubeVideoIdSync(url: string): string | null {
  return fallbackGetYouTubeVideoId(url);
}
